/**
 * AI metadata sidebar panel — read-only display for v1.
 *
 * Codex 2.B.1.design D4: read-only is the v1 boundary. Edits land in a
 * later phase along with prompt-validation + undo semantics.
 *
 * Shows:
 *   - Vendor badge (A1111 / ComfyUI / Midjourney / unknown)
 *   - Prompt
 *   - Negative prompt (A1111)
 *   - Sampler / Seed / Steps / CFG / Model (when available)
 *   - Other key-value pairs (collapsed)
 *   - "Will preserve on PNG export" chip (badge informing the user)
 *
 * Pure DOM, no styling tokens hard-coded — relies on existing
 * .ed-section-* classes. Codex D2: panel is hidden until metadata is
 * actually present, set via showAIPanelTab().
 */

import { t } from '../i18n/index.js';

let mounted = false;
let containerEl = null;
let tabBtnEl = null;
let bodyEl = null;

/** Mount the panel skeleton inside an existing container element.
 *  Idempotent — second call no-ops. */
export function mountAIPanel(containerSelector, tabSelector) {
    if (mounted) return;
    containerEl = typeof containerSelector === 'string'
        ? document.querySelector(containerSelector)
        : containerSelector;
    tabBtnEl = typeof tabSelector === 'string'
        ? document.querySelector(tabSelector)
        : tabSelector;
    if (!containerEl) return;
    bodyEl = document.createElement('div');
    bodyEl.className = 'ai-meta-body';
    containerEl.appendChild(bodyEl);
    mounted = true;
}

/**
 * Render the normalized metadata struct + optional AI-tell diagnostic
 * scores. Tab is shown when EITHER metadata is present OR the
 * aggregate aiLikelihoodScore exceeds the suspicion threshold (Phase
 * 2.B.3 diagnostic-first positioning). Pass both null to clear.
 *
 * @param {object | null} meta    output of extractAIMetadata()
 * @param {object | null} scores  output of computeAITellScores()
 */
export function renderAIMetadata(meta, scores) {
    if (!mounted) return;
    while (bodyEl.firstChild) bodyEl.removeChild(bodyEl.firstChild);
    const TELL_TAB_THRESHOLD = 0.4;
    const tellTrip = scores && Number.isFinite(scores.aiLikelihoodScore)
        && scores.aiLikelihoodScore >= TELL_TAB_THRESHOLD;
    const showTab = (meta && meta.present) || tellTrip;
    if (!showTab) {
        if (tabBtnEl) tabBtnEl.hidden = true;
        return;
    }
    if (tabBtnEl) tabBtnEl.hidden = false;
    /* If meta is absent but scores triggered the tab, we still want a
     * visible header explaining what the user is looking at. */
    if (!meta || !meta.present) {
        const head = makeRow('ai-meta-head');
        head.appendChild(makeChip(t('ai.tellOnly')));
        bodyEl.appendChild(head);
        if (scores) renderTellSection(scores);
        return;
    }

    /* Vendor badge + preserve-on-export chip. */
    const head = makeRow('ai-meta-head');
    head.appendChild(makeChip(t('ai.vendor.' + (meta.vendor || 'unknown')) || meta.vendor));
    head.appendChild(makeChip(t('ai.preservedOnExport'), 'ai-meta-chip-preserve'));
    bodyEl.appendChild(head);

    /* Prompt section. */
    if (meta.prompt) {
        bodyEl.appendChild(makeSection(t('ai.prompt'), meta.prompt));
    }
    if (meta.negativePrompt) {
        bodyEl.appendChild(makeSection(t('ai.negativePrompt'), meta.negativePrompt));
    }

    /* Compact metadata grid (model / sampler / steps / seed / cfg). */
    const gridFields = [];
    if (meta.model)            gridFields.push([t('ai.model'),    meta.model]);
    if (meta.sampler)          gridFields.push([t('ai.sampler'),  meta.sampler]);
    if (meta.steps != null)    gridFields.push([t('ai.steps'),    String(meta.steps)]);
    if (meta.seed != null)     gridFields.push([t('ai.seed'),     String(meta.seed)]);
    if (meta.cfgScale != null) gridFields.push([t('ai.cfg'),      String(meta.cfgScale)]);
    if (gridFields.length) {
        const g = document.createElement('div');
        g.className = 'ai-meta-grid';
        for (const [k, v] of gridFields) {
            const cell = document.createElement('div');
            cell.className = 'ai-meta-cell';
            const lab = document.createElement('div');
            lab.className = 'ai-meta-key';
            lab.textContent = k;
            const val = document.createElement('div');
            val.className = 'ai-meta-val';
            val.textContent = v;
            cell.appendChild(lab); cell.appendChild(val);
            g.appendChild(cell);
        }
        bodyEl.appendChild(g);
    }

    /* "Other" keys collapsed in a details element so they don't clutter. */
    const otherKeys = Object.keys(meta.other || {});
    if (otherKeys.length) {
        const det = document.createElement('details');
        det.className = 'ai-meta-other';
        const sum = document.createElement('summary');
        sum.textContent = t('ai.other');
        det.appendChild(sum);
        for (const k of otherKeys) {
            const row = document.createElement('div');
            row.className = 'ai-meta-cell';
            const lab = document.createElement('div');
            lab.className = 'ai-meta-key';
            lab.textContent = k;
            const val = document.createElement('div');
            val.className = 'ai-meta-val';
            val.textContent = String(meta.other[k]).slice(0, 240);
            row.appendChild(lab); row.appendChild(val);
            det.appendChild(row);
        }
        bodyEl.appendChild(det);
    }

    /* ComfyUI workflow JSON badge — large blob, just expose count of nodes. */
    if (meta.workflow && typeof meta.workflow === 'object') {
        const nodeCount = Object.keys(meta.workflow).length;
        bodyEl.appendChild(makeSection(t('ai.workflow'),
            t('ai.workflowDesc', { count: String(nodeCount) })));
    }

    /* Phase 2.B.2: model-aware color-cast correction button.
     * window.BlrrdAI.matchCast(meta) returns a cast entry or null.
     * We render the button only when a cast is matched. Click routes
     * through window.BlrrdAI.applyCast (defined in bootstrap) which
     * forwards to applyLook(). */
    try {
        if (typeof window !== 'undefined' && window.BlrrdAI
                && typeof window.BlrrdAI.matchCast === 'function') {
            const cast = window.BlrrdAI.matchCast(meta);
            if (cast) {
                const corrEl = document.createElement('div');
                corrEl.className = 'ai-meta-correct';
                const desc = document.createElement('div');
                desc.className = 'ai-meta-correct-desc';
                desc.textContent = t('ai.castDescriptionFor', { name: cast.name });
                if (cast.description) {
                    desc.textContent += ' — ' + cast.description;
                }
                const btn = document.createElement('button');
                btn.className = 'ai-meta-correct-btn';
                btn.textContent = t('ai.applyCast');
                btn.addEventListener('click', () => {
                    if (typeof window.BlrrdAI.applyCast === 'function') {
                        try { window.BlrrdAI.applyCast(cast); }
                        catch (err) { /* swallow — toast is the surface */ }
                    }
                });
                corrEl.appendChild(desc);
                corrEl.appendChild(btn);
                bodyEl.appendChild(corrEl);
            }
        }
    } catch { /* tolerate any missing helpers */ }

    /* Phase 2.B.3 — AI-tell diagnostic scores. Always rendered when
     * provided (even when metadata is also present, since the two
     * convey different things: metadata is "what the file declares",
     * scores are "what the pixels reveal"). */
    if (scores) renderTellSection(scores);
}

function renderTellSection(scores) {
    if (!scores || typeof scores.aiLikelihoodScore !== 'number') return;
    const wrap = document.createElement('div');
    wrap.className = 'ai-meta-tell';
    const lab = document.createElement('div');
    lab.className = 'ai-meta-section-label';
    lab.textContent = t('ai.diagnostic');
    wrap.appendChild(lab);

    const rows = [
        ['ai.tellBanding',   scores.bandingScore],
        ['ai.tellHalo',      scores.haloScore],
        ['ai.tellPlastic',   scores.plasticSkinScore],
        ['ai.tellCombined',  scores.aiLikelihoodScore],
    ];
    for (const [labelKey, score] of rows) {
        const row = document.createElement('div');
        row.className = 'ai-meta-tell-row';
        const tag = document.createElement('div');
        tag.className = 'ai-meta-tell-tag';
        tag.textContent = t(labelKey);
        const bar = document.createElement('div');
        bar.className = 'ai-meta-tell-bar';
        const fill = document.createElement('div');
        fill.className = 'ai-meta-tell-fill';
        const pct = Math.round(Math.max(0, Math.min(1, score)) * 100);
        fill.style.width = pct + '%';
        /* Color by severity: clean → secondary, mild yellow, strong red. */
        if (score >= 0.6)      fill.classList.add('ai-meta-tell-strong');
        else if (score >= 0.3) fill.classList.add('ai-meta-tell-mild');
        bar.appendChild(fill);
        const num = document.createElement('div');
        num.className = 'ai-meta-tell-num';
        num.textContent = pct + '%';
        row.appendChild(tag);
        row.appendChild(bar);
        row.appendChild(num);
        wrap.appendChild(row);
    }
    bodyEl.appendChild(wrap);
}

function makeRow(cls) {
    const el = document.createElement('div');
    el.className = cls;
    return el;
}

function makeChip(text, extra) {
    const el = document.createElement('span');
    el.className = 'ai-meta-chip' + (extra ? ' ' + extra : '');
    el.textContent = text;
    return el;
}

function makeSection(label, body) {
    const wrap = document.createElement('div');
    wrap.className = 'ai-meta-section';
    const lab = document.createElement('div');
    lab.className = 'ai-meta-section-label';
    lab.textContent = label;
    const txt = document.createElement('div');
    txt.className = 'ai-meta-section-body';
    txt.textContent = body;
    wrap.appendChild(lab);
    wrap.appendChild(txt);
    return wrap;
}
